import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { resolveNotificationGames } from "../../constants/notificationGames";
import AdminLayout from "./AdminLayout";
import "./AdminGames.css";

const RANGES = [7, 30];

const GAME_SOURCES = {
  word: {
    id: "word",
    label: "Word Drop",
    scores: "wordDrop_scores",
    boards: "wordDrop_weeklyBoards",
    pushes: "wordDrop_pushSent",
  },
  number: {
    id: "number",
    label: "Number Drop",
    scores: "numberDrop_scores",
    boards: "numberDrop_weeklyBoards",
    pushes: "numberDrop_pushSent",
  },
};

const GAME_OPTIONS = [
  GAME_SOURCES.word,
  GAME_SOURCES.number,
  { id: "combined", label: "Combined" },
];

/** Status badge thresholds (play rate = unique players / fans on board). */
export const STATUS_THRESHOLDS = {
  thriving: { playRate: 0.35, streakDays: 14 },
  healthy: { playRate: 0.25, streakDays: 7 },
  growing: { playRate: 0.15, streakDays: 3 },
  notPlaying: { playRate: 0.05 },
  newMaxAgeDays: 14,
};

function etDateKey(d = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

function addDaysKey(dateKey, delta) {
  const [y, m, day] = dateKey.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, day));
  utc.setUTCDate(utc.getUTCDate() + delta);
  return utc.toISOString().slice(0, 10);
}

function keysInRange(endKey, days) {
  const keys = [];
  for (let i = days - 1; i >= 0; i -= 1) keys.push(addDaysKey(endKey, -i));
  return keys;
}

function pct(n, d) {
  if (!d) return null;
  return Math.round((100 * n) / d);
}

function formatPct(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value}%`;
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function isRealPlayDate(date) {
  return typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date) && date < "2099-01-01";
}

/**
 * Thriving: playRate ≥ 35% AND streak ≥ 14d
 * Healthy:  playRate ≥ 25% AND streak ≥ 7d
 * Growing:  playRate ≥ 15% OR streak ≥ 3d
 * New:      first score within 14 days and not Thriving/Healthy
 * Not playing: playRate < 5% AND streak === 0
 */
export function creatorStatus({ playRate, streakDays, firstSeenDaysAgo }) {
  const rate = Number(playRate) || 0;
  const streak = Number(streakDays) || 0;
  const age = Number(firstSeenDaysAgo);

  if (rate < STATUS_THRESHOLDS.notPlaying.playRate && streak === 0) {
    return "Not playing";
  }
  if (
    rate >= STATUS_THRESHOLDS.thriving.playRate &&
    streak >= STATUS_THRESHOLDS.thriving.streakDays
  ) {
    return "Thriving";
  }
  if (
    rate >= STATUS_THRESHOLDS.healthy.playRate &&
    streak >= STATUS_THRESHOLDS.healthy.streakDays
  ) {
    return "Healthy";
  }
  if (
    Number.isFinite(age) &&
    age <= STATUS_THRESHOLDS.newMaxAgeDays &&
    rate < STATUS_THRESHOLDS.healthy.playRate
  ) {
    return "New";
  }
  if (
    rate >= STATUS_THRESHOLDS.growing.playRate ||
    streak >= STATUS_THRESHOLDS.growing.streakDays
  ) {
    return "Growing";
  }
  if (rate < STATUS_THRESHOLDS.notPlaying.playRate) return "Not playing";
  return "Growing";
}

function statusClass(status) {
  if (status === "Thriving" || status === "Healthy") return "thriving";
  if (status === "Growing" || status === "New") return "growing";
  return "idle";
}

function computeStreak(playDatesSet, endKey) {
  let streak = 0;
  let cursor = endKey;
  // Walk back from endKey; allow starting from most recent play if end has none
  if (!playDatesSet.has(endKey)) {
    for (let i = 1; i <= 60; i += 1) {
      const k = addDaysKey(endKey, -i);
      if (playDatesSet.has(k)) {
        cursor = k;
        break;
      }
      if (i === 60) return 0;
    }
  }
  while (playDatesSet.has(cursor)) {
    streak += 1;
    cursor = addDaysKey(cursor, -1);
  }
  return streak;
}

function buildVerdict({ playRate, d1, finished, playRatePrior, gameLabel = "Word Drop" }) {
  const climbing =
    playRate != null &&
    playRatePrior != null &&
    playRate > playRatePrior;
  if (d1 != null && d1 >= 40 && (climbing || (playRate != null && playRate >= 25))) {
    return climbing
      ? "Play rate and D1 return are both climbing week over week. Keep it."
      : `D1 return is strong and play rate holds — ${gameLabel} is earning its place. Keep it.`;
  }
  if (finished != null && finished >= 60 && d1 != null && d1 >= 25) {
    return "Finish rate is solid and enough players come back the next day. Worth keeping while the board grows.";
  }
  if (playRate != null && playRate < 10) {
    return "Play rate among actives is still thin. Keep shipping, but watch retention before betting the nav slot on it.";
  }
  return "Early signal only — watch play rate, finish rate, and D1 before calling it.";
}

async function fetchScoresInRange(collectionName, startKey, endKey) {
  const snap = await getDocs(
    query(
      collection(db, collectionName),
      where("date", ">=", startKey),
      where("date", "<=", endKey)
    )
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((row) => isRealPlayDate(row.date));
}

async function fetchWeeklyBoardsInRange(collectionName, startKey, endKey) {
  // Board ids are `{creatorId}_{grid}_{weekOf}` with weekOf = Monday date.
  const snap = await getDocs(collection(db, collectionName));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((row) => {
      const weekOf = row.weekOf || String(row.id || "").split("_").pop();
      return typeof weekOf === "string" && weekOf >= startKey && weekOf <= endKey;
    });
}

async function fetchDailyActives(startKey, endKey) {
  // Real DAU field: userPerks.lastActiveDate (updated by trackUserActivity + dailyCheckIn).
  // users.lastActiveDate is NOT maintained by the app — do not use it.
  const snap = await getDocs(
    query(
      collection(db, "userPerks"),
      where("lastActiveDate", ">=", startKey),
      where("lastActiveDate", "<=", endKey)
    )
  );
  return snap.docs.map((d) => ({
    id: d.id,
    lastActiveDate: d.data()?.lastActiveDate || null,
  }));
}

async function fetchUsersByIds(ids) {
  const unique = [...new Set(ids.filter(Boolean))];
  const map = new Map();
  for (const group of chunk(unique, 10)) {
    try {
      const snap = await getDocs(
        query(collection(db, "users"), where(documentId(), "in", group))
      );
      snap.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
    } catch (_) {
      await Promise.all(
        group.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, "users", id));
            if (snap.exists()) map.set(id, { id, ...snap.data() });
          } catch (_) {
            /* skip */
          }
        })
      );
    }
  }
  return map;
}

/**
 * @returns {{ counts: Map<string, number>, available: boolean }}
 */
async function fetchPushSentForCreators(collectionName, creatorIds) {
  const unique = [...new Set(creatorIds.filter(Boolean))];
  const counts = new Map(unique.map((id) => [id, 0]));
  if (!unique.length) return { counts, available: true };

  let available = true;
  await Promise.all(
    unique.map(async (creatorId) => {
      try {
        const snap = await getDocs(
          query(collection(db, collectionName), where("creatorId", "==", creatorId))
        );
        counts.set(creatorId, snap.size);
      } catch (_) {
        available = false;
        counts.set(creatorId, null);
      }
    })
  );
  return { counts, available };
}

async function fetchFollowerCounts(creatorIds, users) {
  const unique = [...new Set(creatorIds.filter(Boolean))];
  const counts = new Map();
  unique.forEach((creatorId) => {
    const fromProfile = Number(users.get(creatorId)?.followerCount);
    counts.set(creatorId, Number.isFinite(fromProfile) ? fromProfile : 0);
  });
  return counts;
}

function aggregateWindow({
  scores,
  perks,
  users,
  pushCounts,
  pushesAvailable,
  followerCounts,
  rangeKeys,
  todayKey,
  priorPlayRate,
  gameLabel,
}) {
  const keySet = new Set(rangeKeys);
  const inWindow = scores.filter((s) => keySet.has(s.date));

  const playersByDay = new Map(rangeKeys.map((k) => [k, new Set()]));
  const activesByDay = new Map(rangeKeys.map((k) => [k, new Set()]));
  perks.forEach((p) => {
    if (activesByDay.has(p.lastActiveDate)) {
      activesByDay.get(p.lastActiveDate).add(p.id);
    }
  });
  inWindow.forEach((s) => {
    if (s.userId) playersByDay.get(s.date)?.add(s.userId);
  });

  // Daily play-rate average across days that have any actives (fallback: days with players)
  let playRateSum = 0;
  let playRateDays = 0;
  rangeKeys.forEach((k) => {
    const actives = activesByDay.get(k)?.size || 0;
    const players = playersByDay.get(k)?.size || 0;
    if (actives > 0) {
      playRateSum += players / actives;
      playRateDays += 1;
    }
  });
  const playRate =
    playRateDays > 0 ? Math.round((100 * playRateSum) / playRateDays) : null;

  const todayPlayers = playersByDay.get(todayKey)?.size || 0;
  const todayActives = activesByDay.get(todayKey)?.size || 0;
  const playedToday =
    todayActives > 0 ? pct(todayPlayers, todayActives) : null;

  const ended = inWindow.filter(
    (s) => s.solved === true || Number(s.guessCount || 0) >= 6
  );
  const finishedCount = ended.filter((s) => s.solved === true).length;
  const finished = ended.length ? pct(finishedCount, ended.length) : null;

  const guessRows = ended.length ? ended : inWindow;
  const guessTotal = guessRows.reduce((sum, s) => sum + (Number(s.guessCount) || 0), 0);
  const avgGuesses =
    guessRows.length > 0 ? Math.round((10 * guessTotal) / guessRows.length) / 10 : null;

  // D1: among user-days with a play on day D in-window, share who also played D+1 (any scores)
  const userDaysAll = new Map();
  scores.forEach((s) => {
    if (!s.userId || !isRealPlayDate(s.date)) return;
    const set = userDaysAll.get(s.userId) || new Set();
    set.add(s.date);
    userDaysAll.set(s.userId, set);
  });
  let d1Eligible = 0;
  let d1Returned = 0;
  userDaysAll.forEach((dates) => {
    dates.forEach((d) => {
      if (!keySet.has(d)) return;
      const next = addDaysKey(d, 1);
      if (next > todayKey) return;
      d1Eligible += 1;
      if (dates.has(next)) d1Returned += 1;
    });
  });
  const d1 = d1Eligible ? pct(d1Returned, d1Eligible) : null;

  const playerIds = [...new Set(inWindow.map((s) => s.userId).filter(Boolean))];
  const notifsOnIds = [];
  playerIds.forEach((uid) => {
    const games = resolveNotificationGames(users.get(uid)?.notifications?.games);
    if (games.enabled) notifsOnIds.push(uid);
  });
  const notifsOn = notifsOnIds.length;
  const notifsPct = playerIds.length ? pct(notifsOn, playerIds.length) : null;

  const uniquePlayers = playerIds.length;
  const uniqueActives = new Set();
  rangeKeys.forEach((k) => {
    activesByDay.get(k)?.forEach((id) => uniqueActives.add(id));
  });
  const playersVsActives =
    uniqueActives.size > 0 ? pct(uniquePlayers, uniqueActives.size) : null;

  const creatorIds = [...new Set(inWindow.map((s) => s.creatorId).filter(Boolean))];
  // Also include creators who only appear outside the window streak calc needs all scores
  const allCreatorIds = [
    ...new Set([
      ...creatorIds,
      ...scores.map((s) => s.creatorId).filter(Boolean),
    ]),
  ];

  const firstSeenGlobal = new Map();
  scores.forEach((s) => {
    if (!s.creatorId || !isRealPlayDate(s.date)) return;
    const prev = firstSeenGlobal.get(s.creatorId);
    if (!prev || s.date < prev) firstSeenGlobal.set(s.creatorId, s.date);
  });

  const rows = creatorIds.map((creatorId) => {
    const creatorScores = inWindow.filter((s) => s.creatorId === creatorId);
    const players = new Set(creatorScores.map((s) => s.userId).filter(Boolean));
    const fans =
      followerCounts.get(creatorId) ??
      Number(users.get(creatorId)?.followerCount) ??
      0;
    const playRateFrac = fans > 0 ? players.size / fans : players.size > 0 ? 1 : 0;
    const playDates = new Set(
      scores.filter((s) => s.creatorId === creatorId && isRealPlayDate(s.date)).map((s) => s.date)
    );
    const streakDays = computeStreak(playDates, todayKey);
    const first = firstSeenGlobal.get(creatorId);
    const firstSeenDaysAgo = first
      ? Math.max(
          0,
          Math.round(
            (Date.parse(`${todayKey}T12:00:00Z`) - Date.parse(`${first}T12:00:00Z`)) /
              86400000
          )
        )
      : 999;
    const status = creatorStatus({
      playRate: playRateFrac,
      streakDays,
      firstSeenDaysAgo,
    });
    const profile = users.get(creatorId);
    const name =
      profile?.displayName ||
      profile?.handle ||
      (creatorId.length > 14 ? `${creatorId.slice(0, 8)}…` : creatorId);

    return {
      creatorId,
      name,
      fans,
      playRate: Math.round(100 * playRateFrac),
      streakDays,
      pushes: pushesAvailable ? pushCounts.get(creatorId) || 0 : null,
      status,
    };
  });

  rows.sort((a, b) => b.playRate - a.playRate || b.streakDays - a.streakDays);

  const verdict = buildVerdict({
    playRate,
    d1,
    finished,
    playRatePrior: priorPlayRate,
    gameLabel: gameLabel || "Word Drop",
  });

  return {
    playedToday,
    playRate,
    finished,
    avgGuesses,
    d1,
    notifsPct,
    playersVsActives,
    uniquePlayers,
    uniqueActives: uniqueActives.size,
    verdict,
    rows,
    allCreatorIds,
    priorPlayRate: priorPlayRate ?? null,
    counts: {
      todayPlayerIds: [...(playersByDay.get(todayKey) || [])],
      todayActiveIds: [...(activesByDay.get(todayKey) || [])],
      playersByDay: Object.fromEntries(
        rangeKeys.map((k) => [k, [...(playersByDay.get(k) || [])]])
      ),
      activesByDay: Object.fromEntries(
        rangeKeys.map((k) => [k, [...(activesByDay.get(k) || [])]])
      ),
      finishedCount,
      endedCount: ended.length,
      guessTotal,
      guessRounds: guessRows.length,
      d1Eligible,
      d1Returned,
      playerIds,
      notifsOnIds,
      activeIds: [...uniqueActives],
    },
  };
}

function unionSize(left, right) {
  return new Set([...(left || []), ...(right || [])]).size;
}

function combinedPlayRate(wordCounts, numberCounts, rangeKeys) {
  let playRateSum = 0;
  let playRateDays = 0;
  rangeKeys.forEach((k) => {
    const actives = unionSize(wordCounts.activesByDay[k], numberCounts.activesByDay[k]);
    const players = unionSize(wordCounts.playersByDay[k], numberCounts.playersByDay[k]);
    if (actives > 0) {
      playRateSum += players / actives;
      playRateDays += 1;
    }
  });
  if (!playRateDays) return { playRate: null, playRateSum: 0, playRateDays: 0 };
  return {
    playRate: Math.round((100 * playRateSum) / playRateDays),
    playRateSum,
    playRateDays,
  };
}

/**
 * Combined headlines recompute each rate from both games' raw counts.
 * Player totals are unions, so the same person is not counted twice.
 * Finish rate and average guesses add rounds, then divide once.
 */
function combineTopLine(word, number, rangeKeys, priorKeys) {
  const wordCounts = word.counts;
  const numberCounts = number.counts;
  const todayPlayers = unionSize(wordCounts.todayPlayerIds, numberCounts.todayPlayerIds);
  const todayActives = unionSize(wordCounts.todayActiveIds, numberCounts.todayActiveIds);
  const playedToday = todayActives ? pct(todayPlayers, todayActives) : null;

  const currentRate = combinedPlayRate(wordCounts, numberCounts, rangeKeys);
  const priorRate = combinedPlayRate(word.priorCounts, number.priorCounts, priorKeys);

  const finishedCount = wordCounts.finishedCount + numberCounts.finishedCount;
  const endedCount = wordCounts.endedCount + numberCounts.endedCount;
  const finished = endedCount ? pct(finishedCount, endedCount) : null;

  const guessTotal = wordCounts.guessTotal + numberCounts.guessTotal;
  const guessRounds = wordCounts.guessRounds + numberCounts.guessRounds;
  const avgGuesses = guessRounds ? Math.round((10 * guessTotal) / guessRounds) / 10 : null;

  const d1Returned = wordCounts.d1Returned + numberCounts.d1Returned;
  const d1Eligible = wordCounts.d1Eligible + numberCounts.d1Eligible;
  const d1 = d1Eligible ? pct(d1Returned, d1Eligible) : null;

  const players = unionSize(wordCounts.playerIds, numberCounts.playerIds);
  const notifsOn = unionSize(wordCounts.notifsOnIds, numberCounts.notifsOnIds);
  const notifsPct = players ? pct(notifsOn, players) : null;

  const actives = unionSize(wordCounts.activeIds, numberCounts.activeIds);
  const playersVsActives = actives ? pct(players, actives) : null;

  return {
    playedToday,
    playRate: currentRate.playRate,
    finished,
    avgGuesses,
    d1,
    notifsPct,
    playersVsActives,
    uniquePlayers: players,
    uniqueActives: actives,
    priorPlayRate: priorRate.playRate,
    verdict: buildVerdict({
      playRate: currentRate.playRate,
      d1,
      finished,
      playRatePrior: priorRate.playRate,
      gameLabel: "Both games",
    }),
    rows: [
      ...word.rows.map((row) => ({
        ...row,
        gameLabel: "Word Drop",
        pushCollection: GAME_SOURCES.word.pushes,
      })),
      ...number.rows.map((row) => ({
        ...row,
        gameLabel: "Number Drop",
        pushCollection: GAME_SOURCES.number.pushes,
      })),
    ],
  };
}

function metricsForGame(bundle, { todayKey, perks, users, rangeDays }) {
  const rangeKeys = keysInRange(todayKey, rangeDays);
  const priorKeys = keysInRange(addDaysKey(rangeKeys[0], -1), rangeDays);
  const shared = {
    scores: bundle.scores,
    perks,
    users,
    pushCounts: bundle.pushCounts,
    pushesAvailable: bundle.pushesAvailable,
    followerCounts: bundle.followerCounts,
    todayKey,
    gameLabel: bundle.label,
  };
  const prior = aggregateWindow({
    ...shared,
    rangeKeys: priorKeys,
    priorPlayRate: null,
  });
  const current = aggregateWindow({
    ...shared,
    rangeKeys,
    priorPlayRate: prior.playRate,
  });
  return { ...current, priorCounts: prior.counts };
}

export default function AdminGames() {
  const [rangeDays, setRangeDays] = useState(7);
  const [game, setGame] = useState("word");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const todayKey = etDateKey();
        // Load 60 days so prior-window play rate + streak have enough history
        const histStart = addDaysKey(todayKey, -59);
        const [wordScores, numberScores, wordBoards, numberBoards, perks] = await Promise.all([
          fetchScoresInRange(GAME_SOURCES.word.scores, histStart, todayKey),
          fetchScoresInRange(GAME_SOURCES.number.scores, histStart, todayKey),
          fetchWeeklyBoardsInRange(GAME_SOURCES.word.boards, histStart, todayKey),
          fetchWeeklyBoardsInRange(GAME_SOURCES.number.boards, histStart, todayKey),
          fetchDailyActives(histStart, todayKey),
        ]);

        const creatorIdsFor = (scores) => [
          ...new Set(scores.map((s) => s.creatorId).filter(Boolean)),
        ];
        const wordCreatorIds = creatorIdsFor(wordScores);
        const numberCreatorIds = creatorIdsFor(numberScores);
        const playerIds = [
          ...new Set(
            [...wordScores, ...numberScores].map((s) => s.userId).filter(Boolean)
          ),
        ];
        const [users, wordPush, numberPush] = await Promise.all([
          fetchUsersByIds([...wordCreatorIds, ...numberCreatorIds, ...playerIds]),
          fetchPushSentForCreators(GAME_SOURCES.word.pushes, wordCreatorIds),
          fetchPushSentForCreators(GAME_SOURCES.number.pushes, numberCreatorIds),
        ]);
        const [wordFollowers, numberFollowers] = await Promise.all([
          fetchFollowerCounts(wordCreatorIds, users),
          fetchFollowerCounts(numberCreatorIds, users),
        ]);

        if (!alive) return;
        setPayload({
          todayKey,
          perks,
          users,
          word: {
            label: GAME_SOURCES.word.label,
            scores: wordScores,
            weeklyBoards: wordBoards,
            pushCounts: wordPush.counts,
            pushesAvailable: wordPush.available,
            followerCounts: wordFollowers,
          },
          number: {
            label: GAME_SOURCES.number.label,
            scores: numberScores,
            weeklyBoards: numberBoards,
            pushCounts: numberPush.counts,
            pushesAvailable: numberPush.available,
            followerCounts: numberFollowers,
          },
        });
      } catch (err) {
        if (!alive) return;
        setError(err?.message || "Could not load games analytics.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const views = useMemo(() => {
    if (!payload) return null;
    const ctx = {
      todayKey: payload.todayKey,
      perks: payload.perks,
      users: payload.users,
      rangeDays,
    };
    const word = metricsForGame(payload.word, ctx);
    const number = metricsForGame(payload.number, ctx);
    const rangeKeys = keysInRange(payload.todayKey, rangeDays);
    const priorKeys = keysInRange(addDaysKey(rangeKeys[0], -1), rangeDays);
    return {
      word,
      number,
      combined: combineTopLine(word, number, rangeKeys, priorKeys),
    };
  }, [payload, rangeDays]);

  const metrics = views?.[game] || null;
  const showGameColumn = game === "combined";
  const colSpan = showGameColumn ? 7 : 6;
  const pageName = game === "number" ? "Number Drop" : game === "combined" ? "Games" : "Word Drop";
  const tableTitle =
    rangeDays === 7 ? "By creator, this week" : "By creator, last 30 days";

  return (
    <AdminLayout>
      <div className="admin-shell admin-games-shell">
        <div className="admin-header-row">
          <div className="admin-page-header">
            <div className="admin-eyebrow">Creator Pipeline</div>
            <div className="admin-page-title">
              {pageName} <em>performance</em>
            </div>
            <span className="admin-page-hand">
              — is it earning its place on the platform?
            </span>
          </div>
          <div className="admin-stat-strip">
            <div className="admin-stat">
              <div className="admin-stat-num num">
                {loading ? "…" : formatPct(metrics?.playedToday)}
              </div>
              <div className="admin-stat-label">Played today</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-num num niche">
                {loading ? "…" : formatPct(metrics?.finished)}
              </div>
              <div className="admin-stat-label">Finished</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-num num gold">
                {loading ? "…" : formatPct(metrics?.d1)}
              </div>
              <div className="admin-stat-label">D1 return</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-num num">
                {loading ? "…" : formatPct(metrics?.notifsPct)}
              </div>
              <div className="admin-stat-label">Notifs on</div>
            </div>
          </div>
        </div>

        <div className="admin-batch-target-card admin-games-verdict">
          <div>
            <div className="admin-batch-target-label">✦ Worth Having?</div>
            <div className="admin-batch-target-value">
              {loading
                ? "Loading live play metrics…"
                : error
                  ? error
                  : metrics?.verdict}
            </div>
          </div>
          <div className="admin-games-verdict-stats">
            <div className="admin-games-verdict-stat">
              <div className="num admin-games-verdict-num">
                {loading || metrics?.avgGuesses == null ? "—" : metrics.avgGuesses}
              </div>
              <div className="admin-games-verdict-label">Avg guesses</div>
            </div>
            <div className="admin-games-verdict-stat">
              <div className="num admin-games-verdict-num">
                {loading ? "—" : formatPct(metrics?.playersVsActives)}
              </div>
              <div className="admin-games-verdict-label">Players / actives</div>
            </div>
            <div className="admin-games-verdict-stat">
              <div className="num admin-games-verdict-num">
                {loading
                  ? "—"
                  : `${metrics?.uniquePlayers ?? 0}/${metrics?.uniqueActives ?? 0}`}
              </div>
              <div className="admin-games-verdict-label">
                {rangeDays}-day headcount
              </div>
            </div>
          </div>
        </div>

        <div className="admin-toolbar">
          <div className="admin-filter-pills" role="group" aria-label="Game">
            {GAME_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`admin-pill${game === option.id ? " active" : ""}`}
                onClick={() => setGame(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="admin-filter-pills" role="group" aria-label="Range">
            {RANGES.map((days) => (
              <button
                key={days}
                type="button"
                className={`admin-pill${rangeDays === days ? " active" : ""}`}
                onClick={() => setRangeDays(days)}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>

        <div className="admin-table-wrap">
          <div className="admin-games-table-title">{tableTitle}</div>
          <table>
            <thead>
              <tr>
                <th>Creator</th>
                {showGameColumn && <th>Game</th>}
                <th>Fans on board</th>
                <th>Play rate</th>
                <th>Streak</th>
                <th>Pushes sent</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={colSpan} className="admin-muted-cell">
                    Loading creators…
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={colSpan} className="admin-muted-cell">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && (!metrics?.rows || metrics.rows.length === 0) && (
                <tr>
                  <td colSpan={colSpan} className="admin-muted-cell">
                    No {pageName === "Games" ? "game" : pageName} scores in this window yet.
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                metrics?.rows?.map((row) => (
                  <tr key={`${row.gameLabel || game}-${row.creatorId}`}>
                    <td className="admin-games-creator">{row.name}</td>
                    {showGameColumn && <td>{row.gameLabel}</td>}
                    <td className="num">{row.fans}</td>
                    <td className="num">{row.playRate}%</td>
                    <td className="num">{row.streakDays}d</td>
                    <td className="num">
                      {row.pushes == null ? (
                        <span
                          className="admin-muted-cell"
                          title={`${row.pushCollection || GAME_SOURCES[game]?.pushes || "pushSent"} read blocked or failed`}
                        >
                          unavailable
                        </span>
                      ) : (
                        row.pushes
                      )}
                    </td>
                    <td>
                      <span className={`admin-games-status ${statusClass(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
